import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useForBindingId } from '../../forBinding/contexts/ForBindingContext';

export const useUserOcrStorage = <T>(baseKey: string, initialValue: T, decode?: (value: unknown) => T) => {
	const { user, isLoading } = useAuth();
	const forBinding = useForBindingId();
	const userId = !isLoading ? user?._id : undefined;
	const profileId = userId ? forBinding ?? userId : undefined;
	const key = profileId ? `${baseKey}:profile:${profileId}` : null;
	const scope = useRef({ key, userId, forBinding });
	if (scope.current.key !== key || scope.current.userId !== userId || scope.current.forBinding !== forBinding) {
		scope.current = { key, userId, forBinding };
	}
	const currentScope = scope.current;
	const read = (): T => {
		if (!key || typeof window === 'undefined') return initialValue;
		try {
			const raw = window.localStorage.getItem(key);
			if (!raw) return initialValue;
			const parsed: unknown = JSON.parse(raw);
			return decode ? decode(parsed) : parsed as T;
		} catch {
			return initialValue;
		}
	};
	const [state, setState] = useState(() => ({ key, value: read() }));
	let value = state.value;
	if (state.key !== key) {
		value = read();
		setState({ key, value });
	}

	const isCurrent = useCallback(
		() => key !== null && scope.current === currentScope,
		[key, currentScope]
	);
	const setValue = useCallback((next: SetStateAction<T>) => {
		if (!isCurrent()) return;
		setState((previous) => {
			if (!isCurrent() || previous.key !== key) return previous;
			return {
				key,
				value: typeof next === 'function'
					? (next as (previous: T) => T)(previous.value)
					: next,
			};
		});
	}, [key, isCurrent]);

	useEffect(() => {
		try {
			window.localStorage.removeItem(baseKey);
			if (!key || !isCurrent()) return;
			if (value === null) window.localStorage.removeItem(key);
			else window.localStorage.setItem(key, JSON.stringify(value));
		} catch {
		}
	}, [baseKey, key, value, isCurrent]);

	return [value, setValue, isCurrent] as const;
};
